/**
 * The app's payment TRANSPORT, handed to core once at boot.
 *
 * The payment engine's design gives each app exactly two responsibilities — "a
 * transport and a ~300-line shell" (CLAUDE.md) — and both apps already have the
 * transport: `apps/{web,mobile}/src/paymentsEngine/transport.ts`. The kiosk's
 * shell is SHARED (`@rista/ui/kiosk`), so the transport is the one half that
 * still has to cross from the app into core, and this is that seam.
 *
 * ⚠️ **Not an `effects-bridge` entry, and the difference is load-bearing.** That
 * file's contract is that every entry degrades to a no-op when unwired, because
 * "dropping a toast is strictly better than turning a successful API response
 * into an exception". A transport is the opposite: it is the only thing that can
 * write the order, so an unwired one must be VISIBLE. `getKioskPaymentTransport`
 * returns null and the payment screen says the kiosk cannot take payment,
 * rather than silently accepting taps that write nothing.
 *
 * It is a plain module singleton for the same reason the store is: an app has
 * one, it is built at boot, and threading it through `KioskApp` as a prop would
 * reverse §13.8 — both containers pass navigation and nothing else, and every
 * other capability a kiosk screen needs it reads through `@rista/core/kiosk/*`.
 *
 * Scoped to the kiosk by its NAME only. Nothing here is kiosk-shaped, so when a
 * second shared surface needs the engine this moves up a level unchanged.
 */
import type { EngineTransport } from "@rista/payments/engine/types";

/**
 * The engine's own calls PLUS whatever provider-specific ones the app hangs off
 * the same object — a provider narrows it with `providerTransport<T>()`, which is
 * why this is deliberately wider than `EngineTransport` and why adding a provider
 * never edits this file.
 */
export type KioskPaymentTransport = EngineTransport & Record<string, any>;

let transport: KioskPaymentTransport | null = null;

/** Call once per app, at boot, beside `initEffectsBridge`. */
export function registerKioskPaymentTransport(next: KioskPaymentTransport): void {
  transport = next;
}

/**
 * REFERENTIALLY STABLE for the life of the app, which the engine requires: the
 * runner is built once per session and closes over whatever it was given on the
 * first render (`usePaymentEngine`'s `PaymentFlowHost` note). A getter that built
 * an object would hand it a snapshot and the failure would be silent and late.
 */
export function getKioskPaymentTransport(): KioskPaymentTransport | null {
  return transport;
}
